import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cartService } from '../services/cart';
import {logout} from './authSlice';

// Async Thunks
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { rejectWithValue }) => {
    const response = await cartService.fetchCart();
    if (response.status >= 400) return rejectWithValue(response.data);
    return response.data;
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (data, { rejectWithValue }) => {
    // console.log("add",data)
    const response = await cartService.addToCart(data);
    if (response.status >= 400) return rejectWithValue(response.data);
    return response.data;
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async (data, { rejectWithValue }) => {
    // console.log("update",data)
    const response = await cartService.updateCartItem(data);
    if (response.status >= 400) return rejectWithValue(response.data);
    return response.data;
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (itemId, { rejectWithValue }) => {
    const response = await cartService.removeFromCart(itemId);
    if (response.status >= 400) return rejectWithValue(response.data);
    return itemId;
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { rejectWithValue }) => {
    const response = await cartService.clearCart();
    if (response.status >= 400) return rejectWithValue(response.data);
    return response.data;
  }
);

export const checkout = createAsyncThunk(
  'cart/checkout',
  async (checkoutData, { rejectWithValue, dispatch }) => {
    const response = await cartService.checkout(checkoutData);
    if (response.status >= 400) return rejectWithValue(response.data);
    // Clear cart after successful checkout
    await dispatch(clearCart());
    return response.data;
  }
);



const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: {},
    loading: false,
    error: null,
    checkoutStatus: null,
  },
  reducers: {
    resetCartError: (state) => {
      state.error = null;
    },
    resetCheckoutStatus: (state) => {
      state.checkoutStatus = null;
    },
    setCartItems: (state, action) => {
      state.items = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("fetchCart", action.payload);
        state.items = action.payload;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        // Use the correct key from your payload to add to items
        state.items[action.payload.id] = action.payload;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Cart Item
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.loading = false;
        // Update the specific item in the items object
        state.items[action.payload.id] = action.payload;
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Remove from Cart
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the specific item from the items object
        delete state.items[action.payload];
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Clear Cart
      .addCase(clearCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.loading = false;
        state.items = {};
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.items = {};
      })
      
      // Checkout
      .addCase(checkout.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.checkoutStatus = null;
      })
      .addCase(checkout.fulfilled, (state) => {
        state.loading = false;
        state.checkoutStatus = 'success';
      })
      .addCase(checkout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.checkoutStatus = 'failed';
      });
  }
});

export const { resetCartError, resetCheckoutStatus, setCartItems } = cartSlice.actions;
export default cartSlice.reducer;